import { analytics } from "@/content";
import { Rich } from "@/lib/rich";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import EmbedFrame, { type EmbedContent } from "@/components/media/EmbedFrame";

export default function AnalyticsDemoReports() {
  const { demoReports } = analytics;
  const { ref: headerRef, revealed: headerRevealed } = useScrollReveal();

  return (
    <section id="demo-reports" className="py-24 bg-white dark:bg-[#0A0A0A] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[5%] left-[-5%] w-[450px] h-[450px] rounded-full bg-blue-100/30 dark:bg-blue-500/[0.10] blur-[140px]" />
        <div className="absolute bottom-[10%] right-[-3%] w-[400px] h-[400px] rounded-full bg-emerald-100/20 dark:bg-emerald-500/[0.06] blur-[130px]" />
        <div className="absolute top-[40%] left-[30%] w-[300px] h-[300px] rounded-full bg-slate-100/40 dark:bg-white/[0.03] blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div
          ref={headerRef}
          className={`text-center mb-16 transition-all duration-700 ${
            headerRevealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-500/[0.12] border border-blue-100 dark:border-blue-400/25 text-blue-700 dark:text-blue-400 text-xs font-semibold tracking-widest uppercase mb-4">
            {demoReports.badge}
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 dark:text-white mb-4">
            <Rich text={demoReports.title} accentClassName="text-blue-600 dark:text-blue-400" />
          </h2>
          {demoReports.subtitle && (
            <p className="text-slate-600 dark:text-white/65 text-lg max-w-2xl mx-auto">
              {demoReports.subtitle}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {demoReports.cards.map((card, index) => (
            <ReportCard key={card.title} card={card} index={index} />
          ))}
        </div>

        <div className="mt-12 text-center">
          <a
            href={demoReports.cta.href}
            className="inline-block px-8 py-4 rounded-xl bg-blue-600 text-white font-bold text-base hover:bg-blue-700 transition-colors cursor-pointer shadow-sm shadow-blue-200"
          >
            {demoReports.cta.label}
          </a>
          <p className="text-slate-500 dark:text-white/60 text-sm mt-3">{demoReports.note}</p>
        </div>
      </div>
    </section>
  );
}

function ReportCard({
  card,
  index,
}: {
  card: { icon: string; title: string; text: string; tags: string[]; demo?: EmbedContent };
  index: number;
}) {
  const { ref, revealed } = useScrollReveal();

  return (
    <div
      ref={ref}
      className={`group bg-white dark:bg-white/[0.04] border border-slate-100 dark:border-white/[0.08] rounded-2xl p-8 shadow-sm hover:-translate-y-1 hover:border-blue-200 hover:shadow-md transition-all duration-300 cursor-default ${
        revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-blue-50 dark:bg-blue-500/[0.12] border border-blue-100 dark:border-blue-400/25 mb-5 transition-all duration-300 group-hover:scale-110 group-hover:bg-blue-100">
        <i className={`${card.icon} text-blue-600 dark:text-blue-400 text-xl`}></i>
      </div>
      <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-3">{card.title}</h3>
      <p className="text-slate-600 dark:text-white/65 text-sm leading-relaxed mb-5">{card.text}</p>
      {card.demo?.enabled && card.demo.url && (
        <EmbedFrame embed={card.demo} className="mb-5" />
      )}
      <div className="flex flex-wrap gap-1.5">
        {card.tags.map((tag) => (
          <span
            key={tag}
            className="px-2.5 py-1 rounded-full bg-slate-50 dark:bg-white/[0.05] text-slate-500 dark:text-white/60 text-[11px] font-medium border border-slate-100 dark:border-white/[0.08]"
          >
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}
